import { SkeletonBox, SkeletonGroup } from '@/components/Skeleton';
import { columnCount, visibleColumns, type ColVis } from '@/lib/contacts';

// Loading skeleton — mirrors the loaded table. Header + colSpan come from the
// same CONTACTS_COLUMNS source as the real table, so they stay in lockstep
// (the old hand-written skeleton header had drifted, missing the Conversion
// column).
export function ContactsPageSkeleton({ colVis }: { colVis: ColVis }) {
  const cols = visibleColumns(colVis);
  const colCount = columnCount(colVis);
  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <SkeletonGroup>
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <SkeletonBox tone="prominent" className="h-10 w-44 sm:h-12" />
            <SkeletonBox className="mt-3 h-4 w-72 max-w-full" />
          </div>
          <SkeletonBox className="h-10 w-36 rounded-lg bg-green-100" />
        </div>
        {/* Chips */}
        <div className="mb-4 flex flex-wrap gap-2">
          {['w-20', 'w-16', 'w-24', 'w-14', 'w-32', 'w-32'].map((w, i) => (
            <SkeletonBox key={i} className={`h-8 rounded-full ${w}`} />
          ))}
        </div>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <SkeletonBox className="h-10 w-full rounded-lg sm:w-72" />
          <div className="flex flex-1 flex-wrap gap-2">
            {['w-20', 'w-16', 'w-16', 'w-24', 'w-24', 'w-20', 'w-20'].map(
              (w, i) => (
                <SkeletonBox key={i} className={`h-8 rounded-full ${w}`} />
              ),
            )}
          </div>
        </div>
        {/* Table */}
        <div className="hidden overflow-hidden rounded-3xl border border-dark/5 bg-card shadow-card md:block">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-dark bg-white text-sm font-bold uppercase tracking-widest text-dark">
                  {cols.map((col) => (
                    <th
                      key={col.id}
                      className="whitespace-nowrap px-6 py-4 text-left"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 7 }).map((_, i) => (
                  <tr
                    key={i}
                    className={i > 0 ? 'border-t-2 border-dark/20' : ''}
                  >
                    {Array.from({ length: colCount }).map((_, j) => (
                      <td key={j} className="px-6 py-4">
                        <SkeletonBox
                          tone={j === 1 ? 'prominent' : 'ambient'}
                          className={`h-4 ${j === 1 ? 'w-32' : 'w-20'}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SkeletonGroup>
    </div>
  );
}
